'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { PROFILE_PAGE_TABS } from '@/src/constants/constants';
import { useBoolean } from '@/src/hooks/use-boolean';
import PageLoader from '@/src/components/ui/PageLoader';
import { ERole, IProfile, IProject, ITile } from 'types/src/types';
import { BitsSection } from './BitsSection';
import { ProjectsSection } from './ProjectsSection';
import dynamic from 'next/dynamic';
import { LoadingCard } from '../../home/components/LoadingCard';
import { SocialPostsSection } from './SocialPostsSection';

const GridLayout = dynamic(
  () =>
    import('./custom-grid/GridLayout').then((mod) => ({
      default: mod.GridLayout,
    })),
  {
    loading: () => (
      <div className="flex flex-wrap gap-5 h-screen w-full">
        {Array.from({ length: 6 }).map((_, index) => (
          <LoadingCard key={index} />
        ))}
      </div>
    ),
    ssr: true,
  }
);

type Props = {
  profile: IProfile;
  isMyProfile: boolean;
  initialTiles: ITile[] | null;
  unpublishedProjects?: IProject[];
};

export const Navigation = ({ profile, isMyProfile, initialTiles, unpublishedProjects }: Props) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const isLoadingTab = useBoolean(false);

  const defaultTab = 'profile';
  const [activeTab, setActiveTab] = useState(defaultTab);
  const renderContent = () => {
    switch (activeTab) {
      case 'profile':
        return (
          <GridLayout
            profile={profile}
            isMyProfile={isMyProfile}
            initialTiles={initialTiles}
          />
        );
      case 'social':
        return <SocialPostsSection userUniqueLink={profile.uniqueLink} />;
      case 'stories':
        return <BitsSection userUniqueLink={profile.uniqueLink} />;
      case 'projects':
        return <ProjectsSection userUniqueLink={profile.uniqueLink} unpublishedProjects={unpublishedProjects} isMyProfile={isMyProfile} />;
      default:
        return (
          <GridLayout
            profile={profile}
            isMyProfile={isMyProfile}
            initialTiles={initialTiles}
          />
        );
    }
  };

  useEffect(() => {
    isLoadingTab.onTrue();
    const tabFromQuery = searchParams.get('tab');
    if (
      tabFromQuery &&
      PROFILE_PAGE_TABS.filter((tab) => tab.id === tabFromQuery).length > 0
    ) {
      setActiveTab(tabFromQuery);
    } else {
      setActiveTab(defaultTab);
    }
    isLoadingTab.onFalse();
  }, [searchParams]);

  const handleTabChange = (tabId: string) => {
    isLoadingTab.onTrue();
    const params = new URLSearchParams(searchParams.toString());
    params.set('tab', tabId);
    router.replace(`?${params.toString()}`);
  };

  return (
    <div className="w-full">
      {isLoadingTab.value && <PageLoader />}
      <div className="flex justify-between items-center md:mb-8 mb-3">
        <div className="flex md:gap-10 gap-5 items-center md:pl-0 pl-3 mt-8 md:mt-0">
          {PROFILE_PAGE_TABS.map((tab) => {
            if (
              profile?.role !== ERole.GAME_DEVELOPER &&
              tab.id === 'projects'
            ) {
              return null;
            }
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`text-base font-normal border-b-2 transition-colors pb-2 ${
                  activeTab === tab.id
                    ? 'border-black text-black'
                    : 'border-transparent text-black/40 hover:text-gray-700 hover:border-gray-300 hover:cursor-pointer'
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {renderContent()}
    </div>
  );
};
